import { getAgentAvatarColor } from '@atlaskit/rovo-agent-components/ui/agent-avatar/GeneratedAvatar';
import { token } from '@atlaskit/tokens';
import { isDefaultRovoChat } from '@atlassian/conversation-assistant-utils/default-agent';

/**
 * Which badge treatment the telepointer renders for an agent.
 *
 * `agent` is a first-party agent (Studio / OOTB / system): an id-derived accent pill with the
 * avatar and no name. `agent-third-party` is a Forge / third-party / remote agent: the neutral
 * grey pill with the avatar and the agent's name.
 */
export type TelepointerAgentKind = 'agent' | 'agent-third-party' | 'fallback' | 'rovo';

/** The agent identifiers shared avatar resolution keys on. */
export interface TelepointerAgentIdentity {
	agentId?: string;
	agentIdentityAccountId?: string | null;
	agentName?: string;
	agentNamedId?: string | null;
	/**
	 * Forge / third-party / remote agent, derived on the producer side from the agent record's
	 * `creator_type`. Third parties get the neutral grey pill and their name; first parties get the
	 * id-derived accent pill and no name.
	 */
	isThirdParty?: boolean;
}

/**
 * The colours the telepointer paints for an agent. Badge, cursor line and content highlight
 * take one hue, so a producer sets all three from this single result.
 */
export interface TelepointerPresentation {
	highlightBackgroundColor: string;
	highlightBorderColor: string;
	telepointerAgentKind: TelepointerAgentKind;
	telepointerLineColor: string;
}

/**
 * Applies an alpha to a colour, resilient to its format (hex / rgb / hsl). Mirrors the
 * streamed-content highlight so the telepointer's highlight fill matches it.
 */
const withAlpha = (color: string, alpha: number): string =>
	`color-mix(in srgb, ${color} ${alpha * 100}%, transparent)`;

/**
 * The neutral grey the fallback badge paints, shared with the third-party pill. For the initial
 * PR a third party takes the same grey as the fallback (differing only by keeping its avatar and
 * name); per-brand third-party colours are a follow-up.
 */
const NEUTRAL_GRAY: Omit<TelepointerPresentation, 'telepointerAgentKind'> = {
	highlightBackgroundColor: token('color.background.accent.gray.subtlest'),
	highlightBorderColor: token('color.border.accent.gray'),
	// A background token, because this paints the badge fill and the cursor line.
	telepointerLineColor: token('color.background.accent.gray.subtler'),
};

/**
 * Resolves how the telepointer badge should present a given agent.
 *
 * Deliberately reads no experiment: the caller decides whether the redesign applies, so this
 * stays a pure function of the agent's identity and can be shared by every producer.
 *
 * A first party takes an id-derived accent pill; a third party (`isThirdParty`) takes the neutral
 * grey pill. The badge itself decides whether to show the name (third party only).
 */
export const resolveTelepointerPresentation = (
	identity?: TelepointerAgentIdentity,
): TelepointerPresentation => {
	// Rovo always presents as itself, so this badge is fixed rather than identity driven.
	if (identity && isDefaultRovoChat(identity)) {
		return {
			highlightBackgroundColor: token('color.background.accent.purple.subtlest'),
			highlightBorderColor: token('color.border.accent.purple'),
			telepointerAgentKind: 'rovo',
			telepointerLineColor: token('color.rovo.background.brand.bold'),
		};
	}

	const { agentId, agentIdentityAccountId, agentNamedId, isThirdParty } = identity ?? {};

	// Any one of the three identifiers shared agent-avatar resolution keys on is enough to
	// draw an avatar, which leaves the neutral badge for a complete absence of all of them.
	if (agentNamedId || agentIdentityAccountId || agentId) {
		// A third party keeps the neutral grey pill (plus its avatar and name); only the first
		// party takes an id-derived accent hue.
		if (isThirdParty) {
			return { ...NEUTRAL_GRAY, telepointerAgentKind: 'agent-third-party' };
		}

		// Follow the design: the telepointer takes the agent's own avatar colour, so the badge,
		// cursor line and content highlight always match the avatar. Uses the avatar palette
		// directly (raw hex, exactly as the avatar illustration does).
		const { primary, secondary } = getAgentAvatarColor({
			agentId,
			agentIdentityAccountId,
			agentNamedId: agentNamedId ?? undefined,
		});

		return {
			highlightBackgroundColor: withAlpha(secondary, 0.2),
			highlightBorderColor: primary,
			telepointerAgentKind: 'agent',
			telepointerLineColor: primary,
		};
	}

	return { ...NEUTRAL_GRAY, telepointerAgentKind: 'fallback' };
};
